import { Flex, Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MEDIA_QUERIES } from 'src/enums';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
import { useTheme } from 'src/styles/theme';
import { IHospital } from 'src/types/hospitals.types';
import { ISRAELI_PHONE_REGEX } from 'src/utils';

import AppCenterModal from '../appCenterModal';
import AppDivider from '../appDivider';
import AppFormItem from '../appFormItem';
import AppInput from '../appInput';
import AppPrimaryButton from '../appPrimaryButton/AppPrimaryButton';
import FullScreenModal from '../fullScreenModal';
import classes from './style/SupportContactModal.module.css';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ISupportContactModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  errorMessage?: string | null;
  isSubmitted: boolean;
  hospital?: IHospital | null;
  onSubmit: (values: {
    hospitalCode: string;
    userName: string;
    phoneNumber: string;
    subject: string;
    content: string;
  }) => void;
  supportModalState: {
    loader: boolean;
    error: null | string;
    success: boolean;
  };
}

const SupportContactModal: React.FunctionComponent<
  ISupportContactModalProps
> = (props) => {
  // PROPS
  const {
    onSubmit,
    isModalVisible,
    closeModal,
    isSubmitted,
    hospital,
    supportModalState
  } = props;
  // TOOLS
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { width } = useWindowDimensions();
  const theme = useTheme();
  const handleFormReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (isSubmitted) {
      handleFormReset();
    }
  }, [isSubmitted]);

  const renderContent = () => {
    return (
      <Flex vertical className={classes.content}>
        <Flex vertical className={classes.form}>
          <Flex justify="center">
            <p
              className={`${theme.fonts.classNames.subPageTitleWidth} ${classes.title} `}
            >
              {t('contactModal.title')}
            </p>
          </Flex>
          <Flex justify="center" vertical style={{ marginTop: 40 }}>
            <Form
              form={form}
              name="basic"
              initialValues={{ remember: true }}
              onFinish={onSubmit}
            >
              <AppFormItem
                aria-label={t('contactModal.name')}
                aria-required="true"
                label={t('contactModal.name')}
                name="userName"
                validateTrigger={'onBlur'}
                rules={[
                  { required: true, message: t('erros.requiredField') },
                  { type: 'string', min: 2, message: t('erros.min2Chars') }
                ]}
              >
                <AppInput label={t('contactModal.name')} />
              </AppFormItem>
              <AppFormItem
                aria-label={t('contactModal.phoneNumber')}
                aria-required="true"
                label={t('contactModal.phoneNumber')}
                name="phoneNumber"
                validateTrigger={'onBlur'}
                rules={[
                  { required: true, message: t('erros.requiredField') },
                  {
                    pattern: ISRAELI_PHONE_REGEX,
                    message: t('erros.invalid_phone_number')
                  }
                ]}
              >
                <AppInput label={t('contactModal.phoneNumber')} />
              </AppFormItem>
              <AppFormItem
                aria-label={t('contactModal.subject')}
                aria-required="true"
                label={t('contactModal.subject')}
                name="subject"
                validateTrigger={'onBlur'}
                rules={[
                  { required: true, message: t('erros.requiredField') },
                  { type: 'string', min: 2, message: t('erros.min2Chars') }
                ]}
              >
                <AppInput label={t('contactModal.subject')} />
              </AppFormItem>
              <AppFormItem
                aria-label={t('contactModal.content')}
                aria-required="true"
                label={t('contactModal.content')}
                name="content"
                validateTrigger={'onBlur'}
                rules={[
                  { required: true, message: t('erros.requiredField') },
                  { type: 'string', min: 2, message: t('erros.min2Chars') }
                ]}
              >
                <div>
                  <TextArea rows={4} placeholder={t('contactModal.content')} />
                </div>
              </AppFormItem>
              <Flex justify="center">
                <AppPrimaryButton
                  disabled={supportModalState.loader}
                  loading={supportModalState.loader}
                  className={classes.contactModalButton}
                  htmlType="submit"
                >
                  {t('contactModal.send')}
                </AppPrimaryButton>
              </Flex>
            </Form>
          </Flex>
        </Flex>
        {getFooter()}
      </Flex>
    );
  };

  const getFooter = () => {
    return (
      <Flex vertical justify="center" className={classes.footer}>
        <AppDivider className={classes.appDivider} mb={0} mt={0} />
        <Flex>
          <img
            src="/assets/contactFooter.png"
            alt="profile_image"
            className={classes.tnxImage}
          />
          <Flex
            vertical
            justify="center"
            className={classes.contactDetailsContainer}
          >
            <Flex className={classes.canReachUsTextContainer}>
              <p className={`${theme.fonts.classNames.pageTitle}`}>
                {t('login.canReachUs')}
              </p>
            </Flex>
            <Flex className={classes.contactDetails}>
              <Flex className={classes.detail}>
                <img src="/assets/icons/phone.png" alt="phone" />
                <p>{hospital?.phone}</p>
              </Flex>
              <Flex className={classes.detail}>
                <img src="/assets/icons/email.png" alt="phone" />
                <p>{hospital?.email}</p>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  const renderModal = () => {
    if ((width ?? 0) < MEDIA_QUERIES.TABLET) {
      return (
        <FullScreenModal visible={isModalVisible} closeModal={closeModal}>
          {renderContent()}
        </FullScreenModal>
      );
    } else {
      return (
        <AppCenterModal visible={isModalVisible} closeModal={closeModal}>
          {renderContent()}
        </AppCenterModal>
      );
    }
  };

  // VIEW
  return renderModal();
};

export default SupportContactModal;
